/**
 * Strona FAQ — Najczęściej zadawane pytania
 * /faq
 */
import { useState, useMemo, useId } from 'react';
import { Search, X, Plus, Minus, MessageCircle } from 'lucide-react';
import { Link } from 'wouter';
import { Footer } from '@/components/sections/Footer';
import { useSeo } from '@/lib/useSeo';
import { PAGE_SEO } from '@/data/pageSeoConfig';
import { PageBreadcrumb } from '@/components/sections/PageBreadcrumb';
import { usePrerenderReady } from '@/hooks/usePrerenderReady';
import { buildFaqJsonLd } from '@/lib/faqJsonLd';
import { CmsSchema, useCmsFields } from '@/cms/content';
import { CmsEditorialText } from '@/components/cms/CmsEditorialText';
import { serializeCmsJson } from '@workspace/url-source/cms';

/* ── Tokens ─────────────────────────────────────────────── */
const GOLD   = '#e05590';
const DARK   = '#2a1f14';
const MUTED  = 'rgba(42,31,20,0.50)';
const LINE   = 'rgba(210,60,120,0.18)';
const CREAM  = '#fff';
const CREAM2 = '#fff';
const CREAM3 = '#fff';

/* ── Category list ───────────────────────────────────────── */
const CATEGORIES = [
  'Wszystkie',
  'Zamówienia',
  'Dostawa',
  'Płatności',
  'Bukiety i kwiaty',
  'Produkty',
  'Bileciki i personalizacja',
  'Odbiór osobisty',
  'Reklamacje i zwroty',
  'Kontakt',
] as const;
type Category = (typeof CATEGORIES)[number];

/* ── FAQ data ────────────────────────────────────────────── */
interface FaqItem { q: string; a: string; cat: Exclude<Category, 'Wszystkie'>; cmsIndex?: number }

const FAQ: FaqItem[] = [
  /* ── ZAMÓWIENIA ── */
  {
    cat: 'Zamówienia',
    q: 'Jak złożyć zamówienie?',
    a: 'Zamówienie możesz złożyć przez naszą stronę internetową bukietowa.pl - wybierz produkt, dodaj go do koszyka, uzupełnij dane dostawy i wybierz sposób płatności. Możesz też zadzwonić do nas bezpośrednio.',
  },
  {
    cat: 'Zamówienia',
    q: 'Czy mogę zamówić kwiaty online?',
    a: 'Tak, zamawianie kwiatów online przez bukietowa.pl jest możliwe przez całą dobę. Zamówienia złożone po godzinach pracy realizujemy następnego dnia roboczego.',
  },
  {
    cat: 'Zamówienia',
    q: 'Czy mogę złożyć zamówienie telefonicznie?',
    a: 'Tak. Skontaktuj się z nami pod numerem +48 605 030 728 (al. Warszawska 23/1 lub ul. Bursztynowa 17/4). Nasi florisci chętnie pomogą w wyborze i złożeniu zamówienia.',
  },
  {
    cat: 'Zamówienia',
    q: 'Czy muszę zakładać konto, żeby zamówić kwiaty?',
    a: 'Nie. Możesz złożyć zamówienie bez rejestracji, jako gość. Zakładając konto zyskujesz dostęp do historii zamówień i możliwość łatwiejszego śledzenia dostaw.',
  },
  {
    cat: 'Zamówienia',
    q: 'Czy mogę złożyć zamówienie bez rejestracji?',
    a: 'Tak, zamawianie jako gość jest w pełni możliwe. Wystarczy podać dane adresowe i kontaktowe podczas składania zamówienia.',
  },
  {
    cat: 'Zamówienia',
    q: 'Czy mogę zmienić zamówienie po jego złożeniu?',
    a: 'Jeśli zamówienie nie zostało jeszcze przekazane do realizacji, możemy je zmodyfikować. Skontaktuj się z nami jak najszybciej - telefonicznie lub przez stronę kontaktową.',
  },
  {
    cat: 'Zamówienia',
    q: 'Czy mogę anulować zamówienie?',
    a: 'Anulowanie zamówienia jest możliwe, jeśli nie zostało ono jeszcze przekazane do realizacji. Skontaktuj się z nami niezwłocznie po złożeniu zamówienia. Po rozpoczęciu przygotowania bukietu anulowanie może nie być możliwe.',
  },
  {
    cat: 'Zamówienia',
    q: 'Czy mogę zamówić kwiaty z wyprzedzeniem?',
    a: 'Tak. Możesz zamówić kwiaty z dowolnym wyprzedzeniem, wybierając preferowaną datę i godzinę dostawy podczas składania zamówienia. Polecamy to rozwiązanie szczególnie przy ważnych okazjach.',
  },

  /* ── DOSTAWA ── */
  {
    cat: 'Dostawa',
    q: 'Gdzie dostarczacie kwiaty?',
    a: 'Dostarczamy kwiaty na terenie Lublina oraz do okolicznych miejscowości, m.in. Świdnika, Jastków, Niemiec, Konopnicy, Nałęczowa, Łęcznej, Lubartowa i wielu innych. Sprawdź pełny zasięg dostawy na stronie „Dostawa".',
  },
  {
    cat: 'Dostawa',
    q: 'Czy dostarczacie kwiaty na terenie całego Lublina?',
    a: 'Tak, dostarczamy kwiaty do wszystkich dzielnic Lublina, w tym Śródmieścia, Czechowa, Czub, Wrotkowa, LSM, Rur, Bronowic, Felina i pozostałych.',
  },
  {
    cat: 'Dostawa',
    q: 'Czy dostarczacie kwiaty poza Lublin?',
    a: 'Tak, realizujemy dostawy do wybranych miejscowości w okolicach Lublina. Koszt dostawy poza miasto może być wyższy - sprawdź kalkulator dostawy na stronie „Dostawa" lub skontaktuj się z nami.',
  },
  {
    cat: 'Dostawa',
    q: 'Ile kosztuje dostawa?',
    a: 'Koszt dostawy zależy od odległości od naszego sklepu. Możesz go sprawdzić za pomocą kalkulatora dostępnego na stronie „Dostawa" - wystarczy wpisać adres docelowy.',
  },
  {
    cat: 'Dostawa',
    q: 'Czy możliwa jest dostawa tego samego dnia?',
    a: 'Tak, jeśli dostępny jest odpowiedni termin. Zamówienia na dziś przyjmujemy od poniedziałku do soboty do 18:00, a w niedzielę do 15:00. Dostępne przedziały sprawdzisz podczas składania zamówienia.',
  },
  {
    cat: 'Dostawa',
    q: 'Do której godziny mogę zamówić kwiaty na dziś?',
    a: 'Od poniedziałku do soboty do 18:00, a w niedzielę do 15:00. Dotyczy to dostawy i odbioru osobistego. Po tej godzinie wybierz kolejny dostępny termin w zamówieniu.',
  },
  {
    cat: 'Dostawa',
    q: 'Czy mogę wybrać godzinę dostawy?',
    a: 'Tak. Dostępne przedziały zależą od dnia i godziny składania zamówienia. Wybierzesz je w formularzu dostawy; ewentualna dopłata jest pokazana przed płatnością. Dokładna godzina zależy od kolejności zamówień i trasy kuriera.',
  },
  {
    cat: 'Dostawa',
    q: 'Czy dostarczacie kwiaty w weekend?',
    a: 'Tak, realizujemy dostawy również w soboty i niedziele. Godziny dostawy w weekendy mogą się nieznacznie różnić - szczegóły znajdziesz przy wyborze terminu w procesie zamówienia.',
  },
  {
    cat: 'Dostawa',
    q: 'Czy dostarczacie kwiaty w święta?',
    a: 'Dni niedostępne do realizacji są wyłączone w kalendarzu zamówienia. Wybierz dostępny termin lub skontaktuj się z nami, aby zapytać o możliwość realizacji.',
  },
  {
    cat: 'Dostawa',
    q: 'Co jeśli odbiorcy nie będzie pod wskazanym adresem?',
    a: 'Kurier podejmie próbę kontaktu z odbiorcą. Jeśli dostarczenie nie będzie możliwe, skontaktujemy się z zamawiającym w celu ustalenia dalszego postępowania. Ponowna próba dostawy wiąże się z dodatkową opłatą.',
  },
  {
    cat: 'Dostawa',
    q: 'Czy kurier kontaktuje się z odbiorcą?',
    a: 'W razie potrzeby kurier może skontaktować się z odbiorcą telefonicznie. Numer telefonu odbiorcy jest wymagany w formularzu dostawy i ułatwia przekazanie zamówienia.',
  },
  {
    cat: 'Dostawa',
    q: 'Czy mogę podać dokładne wskazówki dotyczące dostawy?',
    a: 'Tak. W formularzu zamówienia możesz wpisać dodatkowe informacje dla kuriera, np. numer klatki, kod do domofonu lub inne wskazówki ułatwiające dostarczenie.',
  },
  {
    cat: 'Dostawa',
    q: 'Czy mogę zamówić kwiaty do firmy lub biura?',
    a: 'Tak, dostarczamy kwiaty do firm, biur, restauracji, hoteli i innych lokali na terenie Lublina i okolic. Możliwa jest również regularna współpraca florystyczna - sprawdź naszą ofertę „Dla firm".',
  },
  {
    cat: 'Dostawa',
    q: 'Czy mogę zamówić kwiaty do szpitala?',
    a: 'Tak, dostarczamy kwiaty do szpitali na terenie Lublina. Pamiętaj, że niektóre oddziały mogą mieć ograniczenia dotyczące kwiatów ciętych - warto to sprawdzić wcześniej.',
  },

  /* ── PŁATNOŚCI ── */
  {
    cat: 'Płatności',
    q: 'Jak mogę zapłacić za zamówienie?',
    a: 'Akceptujemy płatności online - kartą kredytową lub debetową. Szczegółowe metody płatności są dostępne w koszyku podczas składania zamówienia.',
  },
  {
    cat: 'Płatności',
    q: 'Czy mogę zapłacić online?',
    a: 'Tak, płatność online jest dostępna przy składaniu zamówienia przez stronę bukietowa.pl. To szybki i bezpieczny sposób na finalizację zakupu.',
  },
  {
    cat: 'Płatności',
    q: 'Czy mogę zapłacić przy odbiorze?',
    a: 'Informacje o dostępnych metodach płatności znajdziesz w koszyku podczas składania zamówienia. W razie wątpliwości skontaktuj się z nami.',
  },
  {
    cat: 'Płatności',
    q: 'Czy otrzymam potwierdzenie płatności?',
    a: 'Tak, po zrealizowaniu płatności automatycznie otrzymasz potwierdzenie na adres e-mail podany podczas składania zamówienia.',
  },
  {
    cat: 'Płatności',
    q: 'Czy otrzymam fakturę?',
    a: 'Tak, na życzenie wystawiamy faktury VAT. Zaznacz tę opcję i podaj dane firmy podczas składania zamówienia lub skontaktuj się z nami po jego złożeniu.',
  },
  {
    cat: 'Płatności',
    q: 'Czy mogę otrzymać fakturę na firmę?',
    a: 'Tak. Jeśli zamawiasz kwiaty dla firmy i potrzebujesz faktury VAT, podaj dane firmy podczas składania zamówienia lub napisz do nas po złożeniu zamówienia.',
  },

  /* ── BUKIETY I KWIATY ── */
  {
    cat: 'Bukiety i kwiaty',
    q: 'Czy bukiety są przygotowywane ze świeżych kwiatów?',
    a: 'Tak. Każdy bukiet przygotowujemy ze świeżych kwiatów dostępnych w danym dniu. Dbamy o jakość i trwałość każdej kompozycji.',
  },
  {
    cat: 'Bukiety i kwiaty',
    q: 'Czy bukiet może różnić się od zdjęcia?',
    a: 'Bukiety na zdjęciach są przykładowe. Ze względu na sezonowość i dostępność kwiatów gotowy bukiet może nieznacznie różnić się od fotografii - zachowując przy tym ten sam charakter i jakość.',
  },
  {
    cat: 'Bukiety i kwiaty',
    q: 'Czy mogę wybrać konkretny kolor kwiatów?',
    a: 'W ramach dostępnych opcji możesz wskazać preferowaną paletę kolorów. Jeśli masz szczególne życzenia, wpisz je w uwagach do zamówienia lub skontaktuj się z nami bezpośrednio.',
  },
  {
    cat: 'Bukiety i kwiaty',
    q: 'Czy mogę poprosić o konkretny gatunek kwiatów?',
    a: 'Staramy się spełniać indywidualne życzenia dotyczące gatunków kwiatów. Dostępność zależy od sezonu i aktualnych dostaw. Wpisz swoje preferencje w uwagach do zamówienia.',
  },
  {
    cat: 'Bukiety i kwiaty',
    q: 'Czy mogę zamówić bukiet według własnego pomysłu?',
    a: 'Tak. Opisz swój pomysł w formularzu zamówienia specjalnego lub zadzwoń do kwiaciarni. Przed zakupem uzgodnimy możliwości wykonania, cenę i termin.',
  },
  {
    cat: 'Bukiety i kwiaty',
    q: 'Czy mogę skorzystać z kreatora bukietów?',
    a: 'Kreator bukietów jest obecnie niedostępny. Możesz wybrać gotowy bukiet ze sklepu albo uzgodnić indywidualną kompozycję z kwiaciarnią przez formularz zamówienia specjalnego lub telefonicznie.',
  },
  {
    cat: 'Bukiety i kwiaty',
    q: 'Czy mogę zamówić bukiet w konkretnym rozmiarze?',
    a: 'Nasze bukiety dostępne są w różnych rozmiarach i wariantach cenowych. Szczegóły dotyczące rozmiarów znajdziesz w opisach produktów. W razie pytań skontaktuj się z nami.',
  },
  {
    cat: 'Bukiety i kwiaty',
    q: 'Czy mogę poprosić o zmianę kwiatów w gotowym bukiecie?',
    a: 'Tak, jeśli zamówienie nie zostało jeszcze przygotowane. Skontaktuj się z nami jak najszybciej po złożeniu zamówienia. Zmiany są możliwe w miarę dostępności kwiatów.',
  },
  {
    cat: 'Bukiety i kwiaty',
    q: 'Czy dostępność kwiatów może się zmienić?',
    a: 'Tak. Dostępność niektórych kwiatów może się zmieniać sezonowo lub z powodu zmian w dostawach. Jeśli wybrany kwiat nie będzie dostępny, zaproponujemy estetyczną alternatywę.',
  },
  {
    cat: 'Bukiety i kwiaty',
    q: 'Jak dbać o świeżość bukietu po jego otrzymaniu?',
    a: 'Skróć łodygi pod kątem, umieść bukiet w czystym wazonie z wodą, w chłodnym miejscu z dala od słońca i przeciągów. Regularnie zmieniaj wodę i usuwaj przekwitłe kwiaty. Do bukietu możemy dołączyć saszetkę z odżywką.',
  },

  /* ── PRODUKTY ── */
  {
    cat: 'Produkty',
    q: 'Czy w ofercie macie flower boxy?',
    a: 'Tak. Flower boxy to eleganckie kompozycje kwiatowe w dekoracyjnych pudełkach - idealne jako prezent. Znajdziesz je w zakładce „Flower Box" na naszej stronie.',
  },
  {
    cat: 'Produkty',
    q: 'Czy macie kwiaty na śluby i uroczystości?',
    a: 'Tak. Oferujemy oprawę florystyczną ślubów i uroczystości - dekoracje sali, bukiety ślubne, wiązanki i inne kompozycje. Szczegóły znajdziesz w zakładce „Ślub" lub skontaktuj się z nami.',
  },
  {
    cat: 'Produkty',
    q: 'Czy macie kwiaty na pogrzeb?',
    a: 'Tak, w ofercie posiadamy kompozycje pogrzebowe - wieńce, wiązanki i inne. Skontaktuj się z nami bezpośrednio, a przygotujemy odpowiednią propozycję.',
  },

  /* ── BILECIKI I PERSONALIZACJA ── */
  {
    cat: 'Bileciki i personalizacja',
    q: 'Czy mogę dodać bilecik do bukietu?',
    a: 'Tak. Przy składaniu zamówienia możesz dodać spersonalizowany bilecik z własną treścią. Bilecik zostanie dołączony do bukietu.',
  },
  {
    cat: 'Bileciki i personalizacja',
    q: 'Czy mogę wpisać własną treść na bileciku?',
    a: 'Tak. Podczas składania zamówienia jest pole, w którym możesz wpisać dowolną treść - życzenia, dedykację lub krótki liścik.',
  },
  {
    cat: 'Bileciki i personalizacja',
    q: 'Czy mogę zamówić kwiaty jako niespodziankę?',
    a: 'Jak najbardziej. Możesz podać adres odbiorcy jako miejsce dostawy i nie informować go wcześniej. Możesz też poprosić kuriera o zachowanie dyskrecji.',
  },
  {
    cat: 'Bileciki i personalizacja',
    q: 'Czy odbiorca dowie się, kto wysłał kwiaty?',
    a: 'Tylko jeśli sam to zaznaczysz - np. w treści bileciku. Jeśli chcesz pozostać anonimowy, po prostu nie podawaj swoich danych na bileciku.',
  },

  /* ── ODBIÓR OSOBISTY ── */
  {
    cat: 'Odbiór osobisty',
    q: 'Czy mogę odebrać zamówienie osobiście?',
    a: 'Tak. Możesz bezpłatnie odebrać zamówienie w jednej z naszych kwiaciarni. Odbiór osobisty jest dostępny w godzinach otwarcia sklepu.',
  },
  {
    cat: 'Odbiór osobisty',
    q: 'Gdzie mogę odebrać zamówienie?',
    a: 'Prowadzimy dwie kwiaciarnie w Lublinie: al. Warszawska 23/1 oraz ul. Bursztynowa 17/4. Dla obu placówek zadzwoń pod +48 605 030 728.',
  },
  {
    cat: 'Odbiór osobisty',
    q: 'Czy mogę wybrać godzinę odbioru?',
    a: 'Tak. W większości przypadków zamówienie jest gotowe tego samego dnia. Możesz wskazać preferowaną godzinę odbioru lub ustalić ją telefonicznie z naszym zespołem.',
  },

  /* ── REKLAMACJE I ZWROTY ── */
  {
    cat: 'Reklamacje i zwroty',
    q: 'Czy mogę zwrócić świeże kwiaty?',
    a: 'Ze względu na szybko psujący się charakter produktu, zwrot świeżych kwiatów i kompozycji jest co do zasady wyłączony na podstawie obowiązujących przepisów prawa. Szczegółowe informacje znajdziesz w naszej Polityce zwrotów.',
  },
  {
    cat: 'Reklamacje i zwroty',
    q: 'Co zrobić, jeśli kwiaty dotrą uszkodzone?',
    a: 'Skontaktuj się z nami niezwłocznie - najlepiej tego samego dnia - i opisz problem. Możesz przesłać zdjęcie. Rozpatrzymy reklamację i zaproponujemy rozwiązanie zgodne z przepisami o niezgodności towaru z umową.',
  },
  {
    cat: 'Reklamacje i zwroty',
    q: 'Co zrobić, jeśli zamówienie jest niezgodne z zamówieniem?',
    a: 'Skontaktuj się z nami jak najszybciej. Prosimy o podanie numeru zamówienia i opisu rozbieżności. Każdą reklamację traktujemy indywidualnie i staramy się rozwiązać ją jak najszybciej.',
  },

  /* ── KONTAKT ── */
  {
    cat: 'Kontakt',
    q: 'Jak skontaktować się z Bukietową?',
    a: 'Możesz zadzwonić pod +48 605 030 728, odwiedzić jedną z naszych kwiaciarni w Lublinie (al. Warszawska 23/1 lub ul. Bursztynowa 17/4) albo skorzystać z formularza kontaktowego dostępnego na stronie „Kontakt".',
  },
  {
    cat: 'Kontakt',
    q: 'Czy mogę zapytać o indywidualne zamówienie?',
    a: 'Tak. Chętnie odpowiemy na pytania dotyczące niestandardowych zamówień - dużych kompozycji, oprawy florystycznej wydarzeń, współpracy dla firm i innych. Skontaktuj się z nami telefonicznie lub przez formularz kontaktowy.',
  },
];

/* ── Diacritic normalizer ────────────────────────────────── */
function normalize(s: string) {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    // Polish-specific manual fallbacks for composed chars
    .replace(/ą/g, 'a').replace(/ć/g, 'c').replace(/ę/g, 'e')
    .replace(/ł/g, 'l').replace(/ń/g, 'n').replace(/ó/g, 'o')
    .replace(/ś/g, 's').replace(/ź/g, 'z').replace(/ż/g, 'z');
}

/* ── Accordion item ──────────────────────────────────────── */
function AccordionItem({ item, highlight }: { item: FaqItem; highlight: string }) {
  const [open, setOpen] = useState(false);
  const id = useId();

  // Highlight matching text
  function hl(text: string) {
    if (!highlight) return text;
    const norm = normalize(text);
    const idx  = norm.indexOf(highlight);
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark style={{ background: `${GOLD}30`, color: DARK, borderRadius: 2, padding: '0 1px' }}>
          {text.slice(idx, idx + highlight.length)}
        </mark>
        {text.slice(idx + highlight.length)}
      </>
    );
  }

  return (
    <div style={{ borderBottom: `1px solid ${LINE}` }}>
      <button
        id={`faq-btn-${id}`}
        aria-expanded={open}
        aria-controls={`faq-panel-${id}`}
        onClick={() => setOpen(v => !v)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 16, padding: 'clamp(16px,2vw,22px) 0',
          background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span data-cms-text={`faq.items.${item.cmsIndex}.q`} style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(16px,1.6vw,19px)', fontWeight: open ? 500 : 400, color: open ? DARK : 'rgba(42,31,20,0.85)', lineHeight: 1.35, flex: 1 }}>
          {hl(item.q)}
        </span>
        <span style={{ flexShrink: 0, width: 28, height: 28, borderRadius: '50%', border: `1px solid ${open ? GOLD : LINE}`, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'border-color 0.2s, background 0.2s', background: open ? `${GOLD}10` : 'transparent' }}>
          {open ? <Minus size={12} color={GOLD} /> : <Plus size={12} color={'rgba(42,31,20,0.5)'} />}
        </span>
      </button>
      {open && (
        <div
          id={`faq-panel-${id}`}
          role="region"
          aria-labelledby={`faq-btn-${id}`}
          style={{ paddingBottom: 'clamp(16px,2vw,22px)', paddingRight: 44 }}
        >
          <p data-cms-text={`faq.items.${item.cmsIndex}.a`} style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(13.5px,1.3vw,15px)', color: MUTED, lineHeight: 1.85, margin: 0 }}>
            {hl(item.a)}
          </p>
        </div>
      )}
    </div>
  );
}

/* ── Page ────────────────────────────────────────────────── */
export default function FaqPage() {
  const cms = useCmsFields(FAQ.flatMap((item, index) => [
    { key: `faq.items.${index}.q`, label: `Pytanie ${index + 1}`, section: item.cat, kind: 'text' as const, defaultValue: item.q, required: true },
    { key: `faq.items.${index}.a`, label: `Odpowiedź ${index + 1}`, section: item.cat, kind: 'textarea' as const, defaultValue: item.a, required: true, optionalMarker: true },
  ]));
  const publishedFaq = FAQ.map((item, index) => ({ ...item, cmsIndex: index, q: cms.value(`faq.items.${index}.q`), a: cms.value(`faq.items.${index}.a`) }));
  usePrerenderReady();
  useSeo(PAGE_SEO.faq);
  const [query, setQuery]     = useState('');
  const [category, setCategory] = useState<Category>('Wszystkie');

  const normQuery = normalize(query.trim());

  const filtered = useMemo(() => {
    return publishedFaq.filter(item => {
      const catMatch = category === 'Wszystkie' || item.cat === category;
      if (!catMatch) return false;
      if (!normQuery) return true;
      return (
        normalize(item.q).includes(normQuery) ||
        normalize(item.a).includes(normQuery) ||
        normalize(item.cat).includes(normQuery)
      );
    });
  }, [normQuery, category, publishedFaq]);

  // Group by category for display
  const groups = useMemo(() => {
    const map = new Map<string, FaqItem[]>();
    for (const item of filtered) {
      if (!map.has(item.cat)) map.set(item.cat, []);
      map.get(item.cat)!.push(item);
    }
    return map;
  }, [filtered]);

  const isEmpty = filtered.length === 0;

  // JSON-LD structured data
  const jsonLd = buildFaqJsonLd(publishedFaq);

  return (
    <div style={{ background: '#fff', minHeight: '100vh' }}>

      <CmsSchema fields={cms.fields} />
      {/* JSON-LD */}
      <script data-cms-jsonld="faq" data-cms-faq-prefix="faq.items" type="application/ld+json" dangerouslySetInnerHTML={{ __html: serializeCmsJson(jsonLd) }} />

      {/* ── Hero ─────────────────────────────────────────── */}
      <section style={{ background: '#fff', borderBottom: `1px solid ${LINE}`, minHeight: 'calc(52dvh - 45px)', display: 'flex', alignItems: 'center', position: 'relative' }}>
        <PageBreadcrumb crumbs={[{ label: 'FAQ' }]} />
        <div style={{ maxWidth: 760, margin: '0 auto', padding: 'calc(var(--page-breadcrumb-height, 44px) + 12px) clamp(20px,5vw,48px) 24px', textAlign: 'center' }}>
          <h1 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2.4rem,5vw,3.6rem)', fontWeight: 400, color: DARK, margin: '0 0 18px', lineHeight: 1.1 }}>
            <CmsEditorialText id="faq.heading" value="Najczęściej zadawane pytania" section="Wstęp" label="Nagłówek" />
          </h1>
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(14px,1.4vw,15.5px)', color: MUTED, maxWidth: 540, margin: '0 auto 32px', lineHeight: 1.75 }}>
            Masz pytanie dotyczące zamówienia, dostawy, płatności lub naszych kwiatów?
            Sprawdź odpowiedzi na najczęściej zadawane pytania.
          </p>

          {/* Search */}
          <div style={{ position: 'relative', maxWidth: 560, margin: '0 auto' }}>
            <Search size={16} color={GOLD} style={{ position: 'absolute', left: 18, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
            <input
              type="search"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Czego szukasz? Np. dostawa, płatność, bukiet..."
              aria-label="Szukaj w FAQ"
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '16px 48px 16px 48px',
                fontFamily: 'DM Sans, sans-serif', fontSize: 14, color: DARK,
                border: `1px solid ${LINE}`, background: '#fff', outline: 'none',
                boxShadow: '0 4px 20px rgba(42,31,20,0.06)',
              }}
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                aria-label="Wyczyść wyszukiwanie"
                style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 4 }}
              >
                <X size={14} color={MUTED} />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* ── Category chips ─────────────────────────────── */}
      <section style={{ background: '#fff', borderBottom: `1px solid ${LINE}`, padding: '20px 0', overflowX: 'auto' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 clamp(20px,5vw,48px)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {CATEGORIES.map(cat => {
            const active = category === cat;
            return (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                aria-pressed={active}
                style={{
                  fontFamily: 'DM Sans, sans-serif', fontSize: 12, letterSpacing: '0.06em',
                  padding: '8px 18px', border: `1px solid ${active ? GOLD : LINE}`,
                  background: active ? `${GOLD}14` : 'transparent',
                  color: active ? DARK : MUTED, cursor: 'pointer',
                  transition: 'all 0.18s', whiteSpace: 'nowrap', borderRadius: 'var(--config-field-radius)',
                  fontWeight: active ? 500 : 400,
                }}
                onMouseEnter={e => { if (!active) { e.currentTarget.style.borderColor = `${GOLD}66`; e.currentTarget.style.color = DARK; } }}
                onMouseLeave={e => { if (!active) { e.currentTarget.style.borderColor = LINE; e.currentTarget.style.color = MUTED; } }}
              >
                {cat}
              </button>
            );
          })}
        </div>
      </section>

      {/* ── Results ────────────────────────────────────── */}
      <section style={{ padding: 'clamp(40px,5vw,72px) 0 clamp(64px,8vw,120px)' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 clamp(20px,5vw,48px)' }}>

          {/* Count */}
          {query && !isEmpty && (
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', color: GOLD, marginBottom: 28 }}>
              Znaleziono {filtered.length} {filtered.length === 1 ? 'odpowiedź' : filtered.length < 5 ? 'odpowiedzi' : 'odpowiedzi'}
            </p>
          )}

          {/* Empty state */}
          {isEmpty && (
            <div style={{ textAlign: 'center', padding: 'clamp(48px,7vw,80px) 0' }}>
              <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.4rem,2.5vw,1.9rem)', fontWeight: 400, color: DARK, marginBottom: 10 }}>
                Nie znaleźliśmy odpowiedzi na Twoje pytanie.
              </p>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 14, color: MUTED, marginBottom: 28 }}>
                Skontaktuj się z nami - pomożemy Ci znaleźć rozwiązanie.
              </p>
              <Link
                href="/kontakt/"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 10,
                  fontFamily: 'DM Sans, sans-serif', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
                  color: '#fff', background: DARK, padding: '13px 28px', textDecoration: 'none',
                }}
              >
                <MessageCircle size={13} /> Skontaktuj się z nami
              </Link>
            </div>
          )}

          {/* FAQ groups */}
          {!isEmpty && (
            <>
              {Array.from(groups.entries()).map(([cat, items]) => (
                <div key={cat} style={{ marginBottom: 'clamp(36px,4vw,56px)' }}>
                  {/* Show category heading when "Wszystkie" or multi-category result */}
                  {(category === 'Wszystkie' || normQuery) && (
                    <h2 style={{
                      fontFamily: 'DM Sans, sans-serif', fontSize: 9.5, letterSpacing: '0.28em', textTransform: 'uppercase',
                      color: GOLD, margin: '0 0 4px', paddingBottom: 14, borderBottom: `2px solid ${GOLD}30`,
                    }}>
                      {cat}
                    </h2>
                  )}
                  <div>
                    {items.map((item, i) => (
                      <AccordionItem key={i} item={item} highlight={normQuery} />
                    ))}
                  </div>
                </div>
              ))}

              {/* Bottom CTA */}
              <div style={{ marginTop: 'clamp(40px,5vw,64px)', padding: 'clamp(28px,3vw,40px)', background: '#fff', border: `1px solid ${LINE}`, textAlign: 'center' }}>
                <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(17px,2vw,21px)', fontWeight: 400, color: DARK, margin: '0 0 10px' }}>
                  Nie znalazłeś odpowiedzi?
                </p>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13.5, color: MUTED, margin: '0 0 20px', lineHeight: 1.7 }}>
                  Napisz lub zadzwoń - chętnie pomożemy.
                </p>
                <Link
                  href="/kontakt/"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 10,
                    fontFamily: 'DM Sans, sans-serif', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
                    color: DARK, border: `1px solid rgba(42,31,20,0.25)`, padding: '12px 26px', textDecoration: 'none',
                  }}
                >
                  <MessageCircle size={13} /> Skontaktuj się z nami
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
